import { ComponentProps, forwardRef } from 'react'
import { BubbleBlock, type CommonBlockProps } from '../../../types'
import { Block } from '../../Block'
import { webchatClasses } from '../../../styles/classes'
import { useModalContext } from '../../../contexts'
import { useTranslations } from '../../../contexts/TranslationContext'
import { ThumbsDown, ThumbsUp } from 'lucide-react'

type Props = BubbleBlock & CommonBlockProps & ComponentProps<'div'>

export const Bubble = forwardRef<HTMLDivElement, Props>(
  (
    {
      block,
      direction,
      sender,
      timestamp,
      messageId,
      feedback,
      metadata,
      isReadOnly,
      sendMessage,
      addMessageFeedback,
      ...props
    },
    ref
  ) => {
    const { showInputModal } = useModalContext()
    const t = useTranslations()

    const {
      message: {
        blocks: {
          bubble: { container, feedbackContainer, feedbackIcon },
        },
      },
    } = webchatClasses

    if (direction == 'incoming') {
      return (
        <div data-direction={'incoming'} data-feedback={!!feedback} {...props} {...container} ref={ref}>
          <Block
            block={{
              ...block,
              direction,
              timestamp,
              sender,
              messageId,
              isReadOnly,
              metadata,
              sendMessage,
            }}
          />

          {addMessageFeedback && showInputModal && block.type === 'text' && (
            <div {...feedbackContainer}>
              <ThumbsUp
                {...feedbackIcon}
                data-selected={feedback === 'positive'}
                onClick={() => addMessageFeedback(messageId, { value: 'positive' })}
              />
              <ThumbsDown
                {...feedbackIcon}
                data-selected={feedback === 'negative'}
                onClick={async () => {
                  const feedbackComment = await showInputModal({
                    title: t['feedback.title'],
                    description: t['feedback.description'],
                    confirmButtonText: t['feedback.confirm'],
                    cancelButtonText: t['feedback.cancel'],
                    Content: FeedbackForm,
                  })

                  addMessageFeedback(messageId, { value: 'negative', comment: feedbackComment })
                }}
              />
            </div>
          )}
        </div>
      )
    } else {
      return (
        <div data-direction={'outgoing'} {...props} {...container} ref={ref}>
          <Block
            block={{
              ...block,
              direction,
              timestamp,
              sender,
              messageId,
              isReadOnly,
              metadata,
              sendMessage,
              addMessageFeedback,
            }}
          />
        </div>
      )
    }
  }
)
Bubble.displayName = 'Bubble'

type FeedbackFormProps = {
  setValue: (comment: string) => void
}

const FeedbackForm = ({ setValue }: FeedbackFormProps) => {
  const {
    modal: {
      dialog: { content },
    },
  } = webchatClasses
  const t = useTranslations()

  return (
    <textarea
      {...content.textarea}
      placeholder={t['feedback.placeholder']}
      onChange={(e) => setValue(e.target.value)}
    ></textarea>
  )
}
